import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDispatch, useStore } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import merge from 'lodash.merge';

import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { mergeStyleWithState } from '../../utils/mergeStyle';
import { transformHtmlString } from '../../utils/transformHtmlString';
import { history } from '../../configureStore';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { pageRequest, submitFormRequest, mergeStyleRequest, updateVariableSuccess, actionRequest } from '../../store/page/actions';
import { buildVariablesData, isVariablesChunk } from '../../utils/variableChunk';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, getStringValueFromPath, getSizesBasedOnScreen, runFunctionsWithTiming, getNestedValue, isClickInsidePortal } from '../../utils/base';
import { buildInternalFns } from '../../utils/handlerScope';
import { InlineText as Type , StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk, Breakpoints, ActionChunk } from '../../types/base';
import generateComponentStyles from '../../utils/styleHelper';
import { useInitEntrance } from '../../utils/useInitEntrance';
import MarkdownContent from '../../chunks/MarkdownContent';

import * as styles from './Text.module.scss';

const StyledInlineText = styled.div.attrs<{ as?: keyof JSX.IntrinsicElements }>(({ as }) => ({
  as, // Dynamically set the tag
}))<{
  $shape?: any;
  $baseScreenResolution?: number;
  $skipStates?: boolean;
  $parentDisplay?: string;
  $parentFlexDirection?: string;
}>`
  z-index: 1;
  position: relative;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates, props.$parentDisplay, undefined, props.$parentFlexDirection)}

  ${({ $shape }) =>
    $shape?.font?.text_overflow === 'ellipsis' &&
    css`
      /* Apply to all nested elements */
      * {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `}
`;


interface InlineTextProps extends Type {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  parentRef?: any;
  parentLoading?: boolean;
  parentStyle?: any;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const InlineText: React.FC<InlineTextProps & { pageSettings: any }> = ({
  id, value, style: stDraft, events: eventsDraft, dataLake,
  pageSettings,
  active_state: activeStateDraft,
  nestedTable,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentRef,
  settings,
  parentLoading,
  parentStyle,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { tag = 'div' } = settings || {};
  const [containerProperties, setContainerProperties] = useState<{ width: number, overflow? : string }>({ width: 0, overflow: undefined });

  const { style: styleDraft, events, active_state, ref: realRef } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ ref, style: stDraft, events: eventsDraft, active_state: activeStateDraft }, dataLake.data) : { ref: undefined, style: stDraft, events: eventsDraft, active_state: activeStateDraft };
  const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
    const { indexes, refs } = chunks || {};
    let mergedStyle = style;
    
    // Apply index-based styles first
    if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
      mergedStyle = merge({}, mergedStyle, indexes[id][index]);
    }
    
    // Apply ref-based updates at the end
    if (refs && realRef) {
      const refUpdate = refs[realRef];
      if (refUpdate?.type && refUpdate.type === 'text') {
        mergedStyle = merge({}, mergedStyle, refUpdate?.update);
      }
    }
    
    return mergedStyle;
  }, [id, realRef]);
  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      
      return (active_state === 'loading' || parentLoading) ? !!((cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal)))) : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, active_state, enableLocalShimmer, valueOriginal, parentLoading, cache]);


  const store = useStore();

  const { effectiveState, entranceAnimation } = useInitEntrance(styleDraft, active_state, containerRef);
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (effectiveState && styleDraft?.states?.[effectiveState]) {
      mergedStyle = mergeStyleWithState({}, styleDraft, styleDraft.states[effectiveState]);
    }

    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[effectiveState]) {
          acc[breakpoint] = mergeStyleWithState({}, typedBreakpointStyle, typedBreakpointStyle.states[effectiveState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    if (entranceAnimation) {
      mergedStyle = { ...mergedStyle, animation: entranceAnimation };
    }
    return mergedStyle;
  }, [styleDraft, effectiveState, entranceAnimation]);

  const style = useMemo(() => mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex), [mergedStyleWithState, styleChunks, parentIndex]);

  const baseScreenResolution = pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const dispatch = useDispatch();
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  // TNBEWEB-1992: shared handler scope (adds data.* / _c.* / _config.* / this.* on top of the
  // legacy $get/$set); `thisRow`/`rowPath` come from the repeatable row context (undefined otherwise).
  const internalFns = buildInternalFns({
    store,
    thisRow: dataLake?.data,
    rowPath: dataLake?.rowPath,
    owner: { id, parentIndex, row: dataLake?.data, activeState: active_state },
  });

  // Variables are assigned before every other chunk in the event (first in the
  // stack). Resolve them synchronously so the style/action/function chunks that
  // follow already see the new values. `{{this.*}}` resolves against this
  // component's own data.
  const applyVariablesFirst = (updates: any[]) => {
    const variableUpdates = (updates || []).filter((u) => isVariablesChunk(u));
    if (!variableUpdates.length) return;
    const state: any = store.getState();
    const data = buildVariablesData(variableUpdates as any, {
      variables: selectVariableData(state),
      localVariables: selectLocalVariableData(state),
      thisContext: dataLake?.data,
      formValues: state.page?.formValues,
    });
    if (data && Object.keys(data).length) {
      dispatch(updateVariableSuccess({ data }));
    }
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[], onSuccessEvts?: any[], onErrorEvts?: any[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id,
        onSuccess: onSuccessEvts,
        onError: onErrorEvts,
      }, filterLoadingState));
    });
  };

  const {
    on_click: clickUpdates,
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_outclick: outclickUpdates,
    on_success: onSuccessUpdates,
    on_error: onErrorUpdates,
  } = events || {};

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (active_state === 'disabled' || active_state === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // A click on an authored link (TNBEWEB-1945) belongs to the link — let it
    // navigate and do NOT also run the node's click actions. Checked here rather
    // than only in MarkdownContent because the ellipsis path renders the content
    // to a string, which drops that component's React handlers.
    if ((e.target as HTMLElement)?.closest?.('a[href]')) {
      return;
    }
    if (clickUpdates) {
      const styleUpdates = clickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = clickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = clickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = clickUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          handleActionChunk(actionUpdates, filterLoadingState, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            const { delay } = config || {};
            if (update?.request && method === 'command') {
              dispatch(pageRequest({
                location: { pathname: value },
                silent,
                componentId: id,
                onSuccess: onSuccessUpdates,
                onError: onErrorUpdates,
              }, filterLoadingState));
            } else if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }, filterLoadingState));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      applyVariablesFirst(clickUpdates);
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);

    }
  }

  const handleHover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply hover
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      applyVariablesFirst(hoverUpdates);
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);
    }
  }

  const handleUnhover = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'touch') return; // TNBEWEB-1980: finger tap — don't apply unhover
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length && (active_state !== 'disabled' && active_state !== 'loading')) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id, onSuccess: onSuccessUpdates, onError: onErrorUpdates }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      applyVariablesFirst(unhoverUpdates);
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects, e);

    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const styleUpdates = launchUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      const runEffects = () => {
        if (styleUpdates.length) {
          dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
        }
        if (actionUpdates.length) {
          handleActionChunk(actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
        }
        if (requestUpdates.length) {
          requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              // const newTimeout = setTimeout(() => {
              //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              // }, config?.delay * 1000 || 0);

              // timeoutsRef.current.push(newTimeout);
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      };
      applyVariablesFirst(launchUpdates);
      runFunctionsWithTiming(functionUpdates, internalFns, runEffects);

    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    if (style?.visibility === 'hidden') return undefined;

    const handleUnclick = (e: MouseEvent) => {
      if (active_state === 'disabled' || active_state === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        containerRef.current && 
        !containerRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        const runEffects = () => {
          // Apply style updates
          if (filteredUpdates.styleUpdates.length) {
            dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
          }

          // Handle action updates
          if (filteredUpdates.actionUpdates.length) {
            handleActionChunk(filteredUpdates.actionUpdates, undefined, onSuccessUpdates, onErrorUpdates);
          }

          // Process request updates
          if (filteredUpdates.requestUpdates.length) {
            filteredUpdates.requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'action') {
                dispatch(submitFormRequest({
                  value,
                  silent,
                  delay,
                  componentId: id,
                  onSuccess: onSuccessUpdates,
                  onError: onErrorUpdates,
                }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
        };
        applyVariablesFirst(outclickUpdates);
        runFunctionsWithTiming(filteredUpdates.functionUpdates, internalFns, runEffects);
      }
    };

    document.addEventListener('mousedown', handleUnclick, true);
    return () => {
      document.removeEventListener('mousedown', handleUnclick, true);
    };
  }, [
    parentIndex, 
    active_state, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
    style?.visibility,
  ]);

  // FE-4974: `value` comes off the screen untyped — the API stores `"value": 12345` — and
  // `?.` doesn't save a number from `.match`. Only a string can carry a `{{this.*}}` token.
  const val = dataLake?.data && typeof value === 'string' && value.match(/{{this\..*}}/)
    ? value.replace(/{{(this\.[^}]+)}}/g, (_, path) => {
        const result = getStringValueFromPath(dataLake.data, path);
        return result ?? '';
      })
    : value;

  const updateContainerWidth = () => {
    if (parentRef?.current && parentRef?.current?.offsetWidth) {
      const styles = window.getComputedStyle(parentRef?.current);
      const paddingLeft = parseFloat(styles.paddingLeft);
      const paddingRight = parseFloat(styles.paddingRight);
      const overflow = styles.overflow || styles.overflowX;
      const totalWidth = parentRef?.current.offsetWidth;
      const available = totalWidth - (paddingLeft + paddingRight);
      setContainerProperties({ width: available, overflow });
      return available;
    }
  };

  useEffect(() => {
    if (!val) return;
      updateContainerWidth();
  }, [parentRef?.current, val]);

  const textOverflowEllipsis = style?.font?.text_overflow === 'ellipsis';

  const TextEl = StyledInlineText;

  const renderMarkdown = <MarkdownContent text={val} />;
  const tryParse = (htmlString: string) => {
    try {
      const sanitizedHtml = DOMPurify.sanitize(htmlString);
      return <span dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
      
    }
    catch (error) {
      console.error('Error parsing HTML string:', error);
      return null;
    }
  }

  if (renderSceleton) {
    const htmlString = ReactDOMServer.renderToString(renderMarkdown);
    const generatedStyle = generateComponentStyles({ font: style?.font });
    const wrappedHtmlString = transformHtmlString(
      htmlString, 
      generatedStyle, containerProperties.width, containerProperties.overflow, textOverflowEllipsis, nestedTable);
  
    const WrappedMarkdownContent = () => wrappedHtmlString ? tryParse(wrappedHtmlString) : null;

    return  (<TextEl
        as={tag}
        ref={containerRef}
        data-id={realRef || id}
        id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
        $shape={style}
        $baseScreenResolution={baseScreenResolution}
        $skipStates={active_state === 'disabled' || active_state === 'loading'}
        $parentDisplay={parentStyle?.display}
        $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
        data-component="text"
        className={combineClassNames(style?.class, styles.fix_overflow)}
        onClick={handleClick}
      onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
      onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      >
         <WrappedMarkdownContent />
      </TextEl>);
  }

  return (
      <TextEl
        ref={containerRef}
        as={tag}
        data-id={realRef || id}
        id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
        $shape={style}
        $baseScreenResolution={baseScreenResolution}
        $skipStates={active_state === 'disabled' || active_state === 'loading'}
        $parentDisplay={parentStyle?.display}
        $parentFlexDirection={typeof parentStyle?.flex === 'object' ? parentStyle?.flex?.direction : undefined}
        data-component="text"
        // FE-4974: a text created through the API may carry no `style` at all.
        className={combineClassNames(style?.class)}
        onClick={handleClick}
        onPointerEnter={hoverUpdates?.length ? handleHover : undefined}
        onPointerLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      >
          <MarkdownContent text={val} />
      </TextEl>
    );
}

export default InlineText;